import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

// Story PS1 (additive): the canonical Overlay/Toast vocabulary (positive/negative/info/
// neutral) now sits alongside the app's original four tones, so canonical-contract
// components can push toasts without a mapping layer. The canonical rule holds: a
// negative toast switches the element to role="alert" so it interrupts; the region stays
// polite. `action` is the canonical one-button slot (usually Undo; anything more belongs
// in a dialog), and onDismiss renders a labelled dismiss control.
export type ToastTone =
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "positive"
  | "negative"
  | "neutral";

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
  /** One button, usually Undo. Anything more belongs in a Modal. */
  action?: ReactNode;
  /** Omit to make the toast non-dismissable - only do that if it auto-expires. */
  onDismiss?: () => void;
  dismissLabel?: string;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_CLASSES: Record<ToastTone, string> = {
  info: "border-primary-200 bg-primary-50 text-primary-700",
  success: "border-accent-200 bg-accent-50 text-accent-700",
  warning: "border-amber-200 bg-amber-50 text-warning",
  danger: "border-red-200 bg-red-50 text-danger",
  positive: "border-accent-200 bg-accent-50 text-accent-700",
  negative: "border-red-200 bg-red-50 text-danger",
  neutral: "border-line bg-surface-muted text-ink-soft",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  const expiryTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const timers = expiryTimers.current;
    return () => { timers.forEach(clearTimeout); timers.clear(); };
  }, []);

  const dismiss = useCallback((id: string) => {
    clearTimeout(expiryTimers.current.get(id));
    expiryTimers.current.delete(id);
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, "id">) => {
      counter.current += 1;
      const id = `toast-${counter.current}`;
      setToasts((current) => [...current, { ...toast, id }]);
      // Long enough to read a two-line message; anything the customer must act on is a
      // dialog or an inline error state, never a toast that can vanish.
      expiryTimers.current.set(id, setTimeout(() => dismiss(id), 6000));
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === "negative" ? "alert" : undefined}
            className={cn(
              "pointer-events-auto animate-fade-in rounded-card border px-4 py-3 shadow-card",
              TONE_CLASSES[toast.tone],
            )}
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{toast.title}</p>
                {toast.body ? <p className="mt-0.5 text-xs opacity-90">{toast.body}</p> : null}
              </div>
              {toast.onDismiss ? (
                <button
                  type="button"
                  aria-label={toast.dismissLabel ?? "Dismiss"}
                  onClick={() => dismiss(toast.id)}
                  className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg hover:bg-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              ) : null}
            </div>
            {toast.action ? <div className="mt-2 flex flex-wrap gap-2">{toast.action}</div> : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside a ToastProvider.");
  return context;
}
